import React from 'react';
import { Card, Row, Col, Typography, Space, Select, DatePicker, message, InputNumber, Input } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { LeftOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useGetMatClntWarehouses, useGetMatClntWhInventory } from '../../../hooks/genericHooks';
import type { MatWarehouses, VwMatClntWhInventory } from '../../../types';
import { fetchSqlDataWithQuery, insertData, getTableNextSequenceValue } from '../../../services/api';
import StandardTable from '../../../components/common/StandardTable';
import MatClntMasterMaterialSelect from '../../../components/modals/MatClntMasterMaterialSelect';
import { useAuth } from '../../../components/common/security/AuthContext';
import Button from '../../../components/common/Button';

const { Title, Text } = Typography;

type ReturnRow = {
  id: number;
  op_type: 'issue' | 'assign';
  txn_date: string;
  itemcode: string;
  qty: number;
  unit: string;
  from_warehouse_id?: number | null;
  to_warehouse_id?: number | null;
  reference_no?: string | null;
  assignee_type?: 'person' | 'company' | null;
  assignee_id?: string | null;
  key: string;
  issued_or_assigned_qty: number;
  already_returned_qty: number;
  returnable_qty: number;
};

export default function MatClntTxnReturn() {
  const navigate = useNavigate();

  // Controls
  const [returnType, setReturnType] = React.useState<'issue' | 'assign'>('issue');
  const [selectedRef, setSelectedRef] = React.useState<string | undefined>(undefined);
  const [returnDate, setReturnDate] = React.useState<string | null>(dayjs().format('YYYY-MM-DD'));
  const { data: whData = [] } = useGetMatClntWarehouses() as any;
  const warehouses: MatWarehouses[] = Array.isArray(whData) ? whData : (whData?.data ?? []);
  const { user: currentUser } = useAuth();
  const [toWhId, setToWhId] = React.useState<number | undefined>(undefined);
  const [notes, setNotes] = React.useState<string>('');
  // Material filter (optional)
  const [selectedItemcode, setSelectedItemcode] = React.useState<string>('');
  const [selectedItemLabel, setSelectedItemLabel] = React.useState<string>('');
  const [clntMasterModalOpen, setClntMasterModalOpen] = React.useState(false);

  // Issued-to code derived from original Issue reference
  const [baseIssuedToCode, setBaseIssuedToCode] = React.useState<string>('');

  // Owned warehouses set for current user (destination)
  const ownedWhSet = React.useMemo(() => {
    const s = new Set<number>();
    (warehouses || []).forEach(w => {
      const owners: string[] = Array.isArray(w?.owners) ? (w.owners as string[]) : [];
      if (currentUser && owners.includes(String(currentUser))) s.add(Number(w.id));
    });
    return s;
  }, [warehouses, currentUser]);
  const whOptions = React.useMemo(() => (warehouses || []).map(w => ({ value: w.id, label: w.name, disabled: !ownedWhSet.has(Number(w.id)) })), [warehouses, ownedWhSet]);
  React.useEffect(() => {
    if (toWhId != null && !ownedWhSet.has(Number(toWhId))) setToWhId(undefined);
  }, [ownedWhSet, toWhId]);

  // Data: inventory description/size map by itemcode
  const { data: invData = [], refetch: refetchInventory } = useGetMatClntWhInventory() as any;
  const invMap = React.useMemo(() => {
    const m = new Map<string, { description?: string | null; size_text?: string | null; unit?: string | null }>();
    const rows: VwMatClntWhInventory[] = Array.isArray(invData) ? invData : (invData?.data ?? []);
    (rows || []).forEach((r: any) => {
      const code = String(r.itemcode || '').trim();
      if (!code) return;
      if (!m.has(code)) m.set(code, { description: r.description, size_text: (r as any)?.size_text ?? null, unit: r.unit });
    });
    return m;
  }, [invData]);

  // Load refs for issue/assign
  const [refOptions, setRefOptions] = React.useState<{ value: string; label: string }[]>([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    const loadRefs = async () => {
      try {
        setLoading(true);
        const matFilter = selectedItemcode ? ` and itemcode = '${selectedItemcode.replace(/'/g, "''")}'` : '';
        const q = `select reference_no from mat_clnt_txns where op_type='${returnType}' and reference_no is not null${matFilter} group by reference_no order by reference_no`;
        const refRows = await fetchSqlDataWithQuery(q);
        const opts = (Array.isArray(refRows) ? refRows : (refRows?.data ?? [])).map((r: any) => ({ value: String(r.reference_no), label: String(r.reference_no) }));
        setRefOptions(opts);
      } catch (e) {
        console.error(e);
        message.error('Failed to load references');
      } finally {
        setLoading(false);
      }
    };
    loadRefs();
    setSelectedRef(undefined);
  }, [returnType, selectedItemcode]);

  // Load transactions for selected reference
  const [tableData, setTableData] = React.useState<ReturnRow[]>([]);
  const [returnQtyById, setReturnQtyById] = React.useState<Record<number, number>>({});

  React.useEffect(() => {
    const loadTxns = async () => {
      if (!selectedRef) { setTableData([]); return; }
      try {
        setLoading(true);
        const matFilter = selectedItemcode ? ` and itemcode = '${selectedItemcode.replace(/'/g, "''")}'` : '';
        const baseQ = `select id, op_type, txn_date, itemcode, qty, unit, from_warehouse_id, to_warehouse_id, reference_no, assignee_type, assignee_id from mat_clnt_txns where op_type='${returnType}' and reference_no='${selectedRef.replace(/'/g, "''")}'${matFilter}`;
        const baseRes = await fetchSqlDataWithQuery(baseQ);
        const baseRows: any[] = Array.isArray(baseRes) ? baseRes : (baseRes?.data ?? []);

        const retQ = `select itemcode, coalesce(sum(qty),0) as returned_qty from mat_clnt_txns where op_type='return' and issued_to like '${selectedRef.replace(/'/g, "''")}:%' group by itemcode`;
        const retRes = await fetchSqlDataWithQuery(retQ);
        const retRows: any[] = Array.isArray(retRes) ? retRes : (retRes?.data ?? []);
        const returnedByItem = new Map<string, number>();
        retRows.forEach(r => returnedByItem.set(String(r.itemcode), Number(r.returned_qty)));

        const enriched: ReturnRow[] = (baseRows || []).map((r: any) => {
          const issuedOrAssigned = Number(r.qty || 0);
          const alreadyReturned = Number(returnedByItem.get(String(r.itemcode)) || 0);
          const returnable = Math.max(0, issuedOrAssigned - alreadyReturned);
          return {
            ...r,
            key: String(r.id),
            issued_or_assigned_qty: issuedOrAssigned,
            already_returned_qty: alreadyReturned,
            returnable_qty: returnable,
          } as ReturnRow;
        });
        setTableData(enriched);

        // Derive original code for Issue/Assign references
        if (returnType === 'issue') {
          const issuedToRes = await fetchSqlDataWithQuery(`select issued_to from mat_clnt_txns where op_type='issue' and reference_no='${selectedRef.replace(/'/g, "''")}' limit 1`);
          const issuedToRow: any = Array.isArray(issuedToRes) ? issuedToRes[0] : (issuedToRes?.data?.[0] ?? null);
          const issuedToVal: string = String(issuedToRow?.issued_to || '').trim();
          // if value like "CODE - NAME", take CODE; otherwise take as-is
          const code = issuedToVal.split(' - ')[0] || '';
          setBaseIssuedToCode(code);
        } else if (returnType === 'assign') {
          const assigneeRes = await fetchSqlDataWithQuery(`select assignee_id from mat_clnt_txns where op_type='assign' and reference_no='${selectedRef.replace(/'/g, "''")}' limit 1`);
          const assigneeRow: any = Array.isArray(assigneeRes) ? assigneeRes[0] : (assigneeRes?.data?.[0] ?? null);
          const code = String(assigneeRow?.assignee_id || '').trim();
          setBaseIssuedToCode(code);
        } else {
          setBaseIssuedToCode('');
        }
      } catch (e) {
        console.error(e);
        message.error('Failed to load transactions for reference');
      } finally {
        setLoading(false);
      }
    };
    loadTxns();
  }, [selectedRef, returnType, selectedItemcode]);

  const columns: ColumnsType<ReturnRow> = React.useMemo(() => [
    { title: 'Item Code', dataIndex: 'itemcode', key: 'itemcode', width: 70 },
    { title: 'Description', key: 'description', width:300, ellipsis: true, render: (_: any, r: ReturnRow) => invMap.get(r.itemcode)?.description || '' },
    { title: 'Size', key: 'size_text', width: 60, render: (_: any, r: ReturnRow) => invMap.get(r.itemcode)?.size_text || '-' },
    { title: returnType === 'issue' ? 'Issued Qty' : 'Assigned Qty', dataIndex: 'issued_or_assigned_qty', key: 'issued_or_assigned_qty', width: 30, align: 'right' },
    { title: 'Returned', dataIndex: 'already_returned_qty', key: 'already_returned_qty', width: 30, align: 'right' },
    { title: 'Returnable', dataIndex: 'returnable_qty', key: 'returnable_qty', width: 30, align: 'right' },
    { title: 'Unit', dataIndex: 'unit', key: 'unit', width: 25 },
    {
      title: 'Return Qty', key: 'return_qty', width: 80, align: 'right',
      render: (_, r) => {
        if ((r.returnable_qty || 0) <= 0) {
          return <Text type="secondary">Completed</Text>;
        }
        return (
          <InputNumber
            min={0}
            max={r.returnable_qty}
            value={returnQtyById[r.id] || 0}
            onChange={v => setReturnQtyById(prev => ({ ...prev, [r.id]: typeof v === 'number' ? v : 0 }))}
            style={{ width: 80 }}
          />
        );
      }
    },
  ], [returnType, returnQtyById, invMap]);

  const handleSubmit = async () => {
    try {
      if (!selectedRef) { message.warning('Select a reference'); return; }
      if (!toWhId) { message.warning('Select destination warehouse'); return; }
      if (!returnDate) { message.warning('Select return date'); return; }

      const nextId = await getTableNextSequenceValue('mat_clnt_txns', 'id');
      if (!nextId) { message.error('Failed to get next transaction id'); return; }
      const retRef = `RET-${String(nextId).padStart(8, '0')}`;

      const payloads = tableData
        .filter(r => Number(returnQtyById[r.id] || 0) > 0)
        .map(r => ({
          op_type: 'return' as const,
          txn_date: returnDate,
          itemcode: r.itemcode,
          qty: Number(returnQtyById[r.id] || 0),
          unit: r.unit,
          to_warehouse_id: toWhId,
          reference_no: retRef,
          issued_to: (baseIssuedToCode ? `${selectedRef}:${baseIssuedToCode}` : undefined),
          notes: notes || undefined,
        }));

      if (payloads.length === 0) { message.warning('Enter quantities to return'); return; }

      await insertData('mat_clnt_txns', payloads);
      try { await (refetchInventory as any)?.(); } catch {}
      message.success(`Return recorded: ${payloads.length} line(s). Ref: ${retRef}`);
      setReturnQtyById({});
      setSelectedRef(undefined);
      setNotes('');
    } catch (e: any) {
      console.error(e);
      message.error(e?.message || 'Failed to submit return');
    }
  };

  return (
    <Card bordered={false} bodyStyle={{ padding: 8 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col>
          <Space align="center" size={8}>
            <Button icon={<LeftOutlined />} onClick={() => navigate(-1)} type="text" aria-label="Back" />
            <div>
              <Title level={4} style={{ margin: 0 }}>Client Materials - Return</Title>
              <Text type="secondary">Return items by reference (Issue or Assign) back to a warehouse</Text>
            </div>
          </Space>
        </Col>
      </Row>

      <Row style={{ marginBottom: 12 }}>
        <Col span={24}>
          <Space wrap>
            <Select
              value={returnType}
              onChange={setReturnType}
              options={[{ value: 'issue', label: 'Issue Reference' }, { value: 'assign', label: 'Assign Reference' }]}
              style={{ width: 180 }}
            />
            <Input
              placeholder="Filter by Item Code (optional)"
              value={selectedItemLabel || selectedItemcode}
              allowClear
              onChange={e => {
                const v = e.target.value;
                // When typing manually, use it as code filter and drop pretty label
                setSelectedItemcode(v);
                setSelectedItemLabel('');
                if (!v) { setSelectedRef(undefined); }
              }}
              onClear={() => { setSelectedItemcode(''); setSelectedItemLabel(''); setSelectedRef(undefined); }}
              style={{ width: 300 }}
              addonAfter={<Button size="small" onClick={() => setClntMasterModalOpen(true)}>Select</Button>}
            />
            <Button size="small" onClick={() => { setSelectedItemcode(''); setSelectedItemLabel(''); setSelectedRef(undefined); }}>Clear</Button>
            <Select
              placeholder="Select Reference"
              allowClear
              showSearch
              value={selectedRef}
              onChange={setSelectedRef as any}
              options={refOptions}
              style={{ width: 280 }}
              filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
            />
            <Select
              placeholder="Destination Warehouse"
              allowClear
              value={toWhId}
              onChange={(v) => setToWhId(v)}
              options={whOptions}
              style={{ width: 260 }}
            />
            <span>
              <Text strong>Return Date</Text>{' '}
              <DatePicker
                value={returnDate ? dayjs(returnDate) : null}
                onChange={(d) => setReturnDate(d ? d.format('YYYY-MM-DD') : null)}
                disabledDate={(d) => !!d && d.isAfter(dayjs(), 'day')}
              />
            </span>
            <Input.TextArea
              placeholder="Notes"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              style={{ width: 320 }}
              rows={1}
            />
            <Button action="add" type="primary" onClick={handleSubmit} loading={loading}>Submit Return</Button>
          </Space>
        </Col>
      </Row>

      {selectedRef ? (
        <StandardTable<ReturnRow>
          columns={columns as any}
          dataSource={tableData}
          rowKey={(r) => r.key}
          loading={loading}
          pagination={{ pageSize: 20, showSizeChanger: true }}
          scroll={{ x: 'max-content' }}
        />
      ) : (
        <Card size="small"><Text type="secondary">Select a reference to see items.</Text></Card>
      )}

      {/* Client Master material selector */}
      <MatClntMasterMaterialSelect
        open={clntMasterModalOpen}
        onCancel={() => setClntMasterModalOpen(false)}
        onOk={(rows) => {
          const first = Array.isArray(rows) && rows.length > 0 ? rows[0] : null;
          if (first) {
            setSelectedItemcode(first.itemcode);
            setSelectedItemLabel(`${first.itemcode} - ${first.description || ''}`);
            setSelectedRef(undefined);
          }
          setClntMasterModalOpen(false);
        }}
        title="Select Client Material"
      />

      {/* no returning person picker; issued_to comes from original issue */}
    </Card>
  );
}
